import { forwardRef, useMemo } from 'react'
import { Select, type SelectProps } from './Select.js'

/**
 * Canada's zones first, then every other zone the browser knows.
 *
 * A city is added for one of six Canadian time zones almost every time, and
 * hunting for `America/Toronto` in a list of six hundred is a small daily tax on
 * somebody who adds cities for a living. The rest stay available underneath
 * because the platform is not promised to stop at the border.
 */
const CANADA = [
  'America/St_Johns',
  'America/Halifax',
  'America/Toronto',
  'America/Winnipeg',
  'America/Edmonton',
  'America/Vancouver',
]

/** What the zone is doing right now — "Toronto · UTC−04:00". */
function describe(zone: string, now: Date): string {
  const city = zone.split('/').pop()?.replace(/_/g, ' ') ?? zone
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(now)
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value
    return offset ? `${city} · ${offset}` : city
  } catch {
    return city
  }
}

export interface TimezoneSelectProps extends Omit<SelectProps, 'options'> {
  /** Zones to float to the top. Defaults to Canada's six. */
  preferred?: string[]
}

/**
 * Which clock a city runs on.
 *
 * Adding or editing a city takes an IANA zone name, and it is the one field on
 * that form nobody can be expected to type from memory — `America/Toronto`, not
 * "Eastern", and certainly not "EST", which is wrong for half the year.
 *
 * The list is the browser's own (`Intl.supportedValuesOf`), so it stays correct
 * as zones change without anybody shipping a new build. Each row shows the city
 * and what the offset is *today*, which is how somebody checks they picked the
 * right one.
 */
export const TimezoneSelect = forwardRef<HTMLSelectElement, TimezoneSelectProps>(
  function TimezoneSelect({ preferred = CANADA, ...props }, ref) {
    const options = useMemo(() => {
      const now = new Date()
      const all: string[] =
        typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
      const top = preferred.filter((z) => all.length === 0 || all.includes(z))
      const rest = all.filter((z) => !top.includes(z))
      // A separator rather than two <optgroup>s: a disabled option reads as a
      // heading to a screen reader and cannot be chosen by accident.
      return [
        ...top.map((z) => ({ value: z, label: describe(z, now) })),
        ...(rest.length ? [{ value: '', label: '──────────', disabled: true }] : []),
        ...rest.map((z) => ({ value: z, label: `${describe(z, now)} (${z})` })),
      ]
    }, [preferred])

    return <Select ref={ref} options={options} placeholder="Choose a time zone" {...props} />
  },
)
